import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Category as CategoriesIcon,
  Group as GroupIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  MonetizationOn as MonetizationOnIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from 'recharts'
import LoadingSpinner from '../components/LoadingSpinner'
import { statsService } from '../services/api'

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0)

const formatNumber = (value = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(value) || 0)

const variationBadge = (value) => {
  if (value === null || value === undefined) return null
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return null
  const isPositive = numeric >= 0
  return (
    <Chip
      icon={isPositive ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
      label={`${Math.abs(numeric).toFixed(1)} % vs mois dernier`}
      size="small"
      color={isPositive ? 'success' : 'error'}
      sx={{ mt: 1 }}
    />
  )
}

const StatCard = ({ icon, title, value, helper, variation, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {helper && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {helper}
            </Typography>
          )}
          {variationBadge(variation)}
        </Box>
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette[color]?.light || theme.palette.primary.light,
            color: (theme) => theme.palette[color]?.main || theme.palette.primary.main,
            borderRadius: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.5,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
)

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const response = await statsService.getGlobalStats()
        setStats(response.data?.data || null)
      } catch (err) {
        console.error('Erreur chargement stats dashboard:', err)
        setError("Impossible de récupérer les statistiques")
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const evolutionData = useMemo(() => {
    if (!stats?.evolution?.last30Days) return []
    return stats.evolution.last30Days.map((entry) => ({
      date: dayjs(entry.date).format('DD/MM'),
      orders: entry.orders,
      revenue: Number(entry.revenue || 0),
    }))
  }, [stats])

  const topProducts = (stats?.products?.topSelling || []).map((p) => ({
    productName: p.name,
    variantName: p.variantName,
    displayName: p.variantName ? `${p.name} — ${p.variantName}` : p.name,
    totalSold: p.totalSold,
  }))
  const topReferrers = stats?.referrals?.topReferrers || []
  const topLoyalty = stats?.loyalty?.topUsers || []

  const revenueCards = [
    {
      title: "Chiffre d'affaires total",
      value: formatCurrency(stats?.revenue?.total),
      helper: `Panier moyen ${formatCurrency(stats?.revenue?.avgOrderValue)}`,
      icon: <MonetizationOnIcon fontSize="large" />,
      color: 'success',
      variation: null,
    },
    {
      title: "Revenu ce mois",
      value: formatCurrency(stats?.revenue?.thisMonth),
      helper: `${formatNumber(stats?.orders?.thisMonth)} commandes (hors annulées)`,
      icon: <TrendingUpIcon fontSize="large" />,
      color: 'info',
      variation: null,
    },
    {
      title: 'Commandes totales',
      value: formatNumber(stats?.orders?.total),
      helper: `Hors annulées : ${formatNumber(stats?.orders?.paid)} (${stats?.orders?.conversionRate || 0}% conv.)`,
      icon: <ShoppingCartIcon fontSize="large" />,
      color: 'primary',
      variation: null,
    },
    {
      title: 'Utilisateurs',
      value: formatNumber(stats?.users?.total),
      helper: `${formatNumber(stats?.users?.thisMonth)} nouveaux ce mois`,
      icon: <PeopleIcon fontSize="large" />,
      color: 'secondary',
      variation: stats?.users?.conversionRate || null,
    },
  ]

  const catalogueCards = [
    {
      title: 'Produits actifs',
      value: formatNumber(stats?.products?.total),
      helper:
        `${formatNumber(stats?.products?.inStock)} variantes en stock` +
        (stats?.products?.outOfStock
          ? ` • ${formatNumber(stats?.products?.outOfStock)} en rupture`
          : ''),
      icon: <InventoryIcon fontSize="large" />,
      color: 'primary',
    },
    {
      title: 'Catégories',
      value: formatNumber(stats?.products?.categories || 0),
      helper: `${formatNumber(stats?.products?.subcategories || 0)} sous-catégories`,
      icon: <CategoriesIcon fontSize="large" />,
      color: 'secondary',
    },
    {
      title: 'Promotions actives',
      value: formatNumber(stats?.promotions?.active || 0),
      helper: 'Campagnes marketing en cours',
      icon: <LocalOfferIcon fontSize="large" />,
      color: 'warning',
    },
  ]

  const cartCards = [
    {
      title: 'Paniers actifs',
      value: formatNumber(stats?.carts?.active || 0),
      helper: `${formatCurrency(stats?.carts?.totalValue)} en valeur totale`,
      icon: <ShoppingCartIcon fontSize="large" />,
      color: 'info',
    },
    {
      title: 'Affiliés actifs',
      value: formatNumber(stats?.affiliates?.active || 0),
      helper: `${formatNumber(stats?.affiliates?.total || 0)} affiliés`,
      icon: <GroupIcon fontSize="large" />,
      color: 'success',
    },
    {
      title: 'Parrainages validés',
      value: formatNumber(stats?.referrals?.validated || 0),
      helper: `${stats?.referrals?.validationRate || 0}% validation`,
      icon: <StarIcon fontSize="large" />,
      color: 'secondary',
    },
  ]

  if (loading) {
    return <LoadingSpinner message="Chargement du tableau de bord..." />
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Tableau de bord
        </Typography>
        <Paper sx={{ p: 4 }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Tableau de bord
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dernière actualisation : {dayjs().format('DD MMM YYYY HH:mm')}
          </Typography>
        </Box>
        <Tooltip title="Rafraîchir les données">
          <span>
            <IconButton
              onClick={async () => {
                setLoading(true)
                setError('')
                try {
                  const response = await statsService.getGlobalStats()
                  setStats(response.data?.data || null)
                } catch (err) {
                  console.error(err)
                  setError('Impossible de mettre à jour les statistiques')
                } finally {
                  setLoading(false)
                }
              }}
              disabled={loading}
            >
              {loading ? <Skeleton variant="circular" width={32} height={32} /> : <TrendingUpIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Grid container spacing={3}>
        {revenueCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Évolution des commandes (30 derniers jours)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2F3B4C" />
                  <XAxis dataKey="date" stroke="#8E9AAF" />
                  <YAxis stroke="#8E9AAF" />
                  <RechartsTooltip formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name === 'revenue' ? 'CA' : 'Commandes']} />
                  <Legend />
                  <Line type="monotone" dataKey="orders" stroke="#64B5F6" strokeWidth={2} dot={false} name="Commandes" />
                  <Line type="monotone" dataKey="revenue" stroke="#81C784" strokeWidth={2} dot={false} name="CA" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Produit les plus vendus
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProducts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2F3B4C" />
                  <XAxis dataKey="displayName" stroke="#8E9AAF" hide />
                  <YAxis stroke="#8E9AAF" />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: 8,
                      color: '#f9fafb',
                    }}
                    labelStyle={{ color: '#f9fafb' }}
                    formatter={(value) => [`${value}`, 'Ventes']}
                    labelFormatter={(label, payload) => {
                      if (!payload || payload.length === 0) return label
                      const entry = payload[0]?.payload
                      if (!entry) return label
                      return entry.variantName
                        ? `${entry.productName} — ${entry.variantName}`
                        : entry.productName || label
                    }}
                  />
                  <Bar dataKey="totalSold" fill="#9575CD" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="totalSold" position="top" fill="#f9fafb" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {catalogueCards.map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <StatCard {...card} variation={null} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Parrains les plus performants
              </Typography>
              <List>
                {topReferrers.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Aucune donnée disponible" />
                  </ListItem>
                )}
                {topReferrers.map((referrer) => (
                  <React.Fragment key={referrer.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar>{referrer.name?.charAt(0) || '?'}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={referrer.name || 'Utilisateur'}
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Code: {referrer.code || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Parrainages: {formatNumber(referrer.totalReferrals || 0)} • Points: {formatNumber(referrer.points || 0)} • XP: {formatNumber(referrer.xp || 0)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top membres fidélité
              </Typography>
              <List>
                {topLoyalty.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Aucune donnée disponible" />
                  </ListItem>
                )}
                {topLoyalty.map((user) => (
                  <React.Fragment key={user.userId}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar src={user.avatar}>{user.name?.charAt(0) || '?'}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name || 'Utilisateur'}
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Niveau {user.level} • XP {formatNumber(user.xp || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Points: {formatNumber(user.points || 0)} • {user.email || '—'}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1, mb: 4 }}>
        {cartCards.map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <StatCard {...card} variation={null} />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default Dashboard
